// Module 12 — performance HUD. A small corner readout of the feedback signals the adaptive system
// balances each second, so the user can SEE the trade it is making:
//   • render resolution — real drawing-buffer pixels + the buffer-px-per-CSS-px scale (4K vs downscaled)
//   • frame rate        — smoothed main-thread FPS + the currently active quality tier + the governor
//                         MODE (STABLE / DEGRADED / PROBING↑) so the user knows WHY it picked this tier
//   • simulation rate   — the engine's ACHIEVED ticks/second (the governor's pace knob)
//   • ambient           — battery level + charging state + network class + Save-Data (the multi-layered
//                         env signals the v2 start-tier + re-probe consider)
// Pure DOM + an injectable interval. It only READS non-contract introspection getters (governor.fps /
// .quality/.mode, viz._debug.renderResolution, the latest snapshot's tick, the env snapshot), so it can
// never perturb the command stream or snapshot sequence — determinism (R15) is untouched. Absent markup
// ⇒ silent no-op, matching the other index.html wirings (sun-clock, legend), so headless boots don't break.

import type { EnvSignals } from "./env-signals";
import type { BatteryState } from "./env-signals";
import type { GovernorMode } from "./governor";

export interface PerfHudDeps {
  root: HTMLElement | null; // the #perf-hud container; null/absent → no-op
  getFps: () => number; // governor.fps (smoothed main-thread FPS)
  getQuality: () => string; // governor.quality (ultra|highplus|high|mediumplus|medium|low)
  getMode?: (() => GovernorMode) | undefined; // governor.mode (stable|degraded|probing) — v2
  getResolution: () => { width: number; height: number; renderScale: number; dpr: number };
  getTick: () => number | null; // absolute (monotonic) sim tick of the latest snapshot; null pre-boot
  getEnv?: (() => EnvSignals) | undefined; // the ambient signals snapshot — v2
  getBattery?: (() => BatteryState | null) | undefined; // live battery state (async-updated) — v2
  intervalMs?: number; // refresh cadence (default 500ms)
  setIntervalFn?: (cb: () => void, ms: number) => number; // injectable for node tests
  clearIntervalFn?: (id: number) => void;
  now?: () => number; // ms clock (default performance.now)
}

export interface PerfHud {
  stop(): void;
  refresh(): void; // exposed so tests can step it deterministically
}

function fmtScale(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1);
}

/** Short human label for the governor mode, with an arrow when it is actively climbing. */
function modeLabel(mode: GovernorMode | undefined): string {
  if (mode === undefined) return "";
  if (mode === "probing") return " · PROBING↑";
  if (mode === "degraded") return " · DEGRADED";
  return " · STABLE";
}

/** Compose the ambient line: battery + network + Save-Data. Empty string when no ambient signal exists. */
function ambientLine(env: EnvSignals | undefined, battery: BatteryState | null): string {
  if (!env) return "";
  const parts: string[] = [];
  const b = battery ?? env.battery;
  if (b) {
    const pct = Math.round((b.level ?? 0) * 100);
    parts.push(b.charging ? `⚡${pct}%` : `🔋${pct}%`);
  }
  if (env.saveData) parts.push("Save-Data");
  if (env.effectiveType !== "unknown" && env.effectiveType !== "4g") parts.push(`📶${env.effectiveType}`);
  return parts.length > 0 ? " · " + parts.join(" ") : "";
}

// Find a labelled value span, or create one under the container so the HUD still renders if index.html
// only supplies the bare #perf-hud shell.
function valueEl(root: HTMLElement, key: string): HTMLElement {
  const found = root.querySelector<HTMLElement>(`[data-perf="${key}"]`);
  if (found) return found;
  const doc = root.ownerDocument;
  const el = doc.createElement("span");
  el.setAttribute("data-perf", key);
  el.className = `perf-line perf-${key}`;
  root.appendChild(el);
  return el;
}

export function mountPerfHud(deps: PerfHudDeps): PerfHud {
  const root = deps.root;
  if (!root) return { stop() { /* no-op */ }, refresh() { /* no-op */ } };

  const resEl = valueEl(root, "res");
  const fpsEl = valueEl(root, "fps");
  const simEl = valueEl(root, "sim");
  const ambEl = valueEl(root, "amb");
  const now = deps.now ?? ((): number => performance.now());
  const setI = deps.setIntervalFn ?? ((cb, ms): number => setInterval(cb, ms) as unknown as number);
  const clrI = deps.clearIntervalFn ?? ((id): void => clearInterval(id));

  let prevTick: number | null = null;
  let prevT = now();
  let hz = 0; // lightly-smoothed achieved sim rate

  const refresh = (): void => {
    const r = deps.getResolution();
    // width 0 before the first painted frame (or under the test fake) — show the scale only.
    resEl.textContent = r.width > 0 ? `${r.width}×${r.height} · ${fmtScale(r.renderScale)}×` : `${fmtScale(r.renderScale)}×`;
    fpsEl.textContent = `${deps.getFps()} FPS · ${deps.getQuality().toUpperCase()}${modeLabel(deps.getMode?.())}`;

    // sim Hz from monotonic tick deltas over WALL time. tick is absolute, so this is robust to the
    // render coalescing in compose (which can apply only the newest snapshot per frame): Δtick/Δt is
    // still the true engine rate. EMA-smoothed so a slow refresh tick doesn't make it jump.
    const tick = deps.getTick();
    const t = now();
    if (tick !== null && prevTick !== null && t > prevT) {
      const inst = Math.max(0, (tick - prevTick) / ((t - prevT) / 1000));
      hz = hz === 0 ? inst : hz * 0.6 + inst * 0.4;
    }
    if (tick !== null) { prevTick = tick; prevT = t; }
    simEl.textContent = `${hz.toFixed(1)} Hz sim`;

    // ambient line (v2): battery + network + Save-Data — the multi-layered env signals.
    const env = deps.getEnv?.();
    const bat = deps.getBattery?.() ?? null;
    const amb = ambientLine(env, bat);
    ambEl.textContent = amb;
    ambEl.hidden = amb === "";
  };

  refresh();
  const id = setI(refresh, deps.intervalMs ?? 500);
  return { stop(): void { clrI(id); }, refresh };
}
